import { formatDate } from '../utils/formatUtils';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAPIUrl } from '../api/apiUtils';
import { getGroupTitleById } from '../utils/groupTitles';
import AnomalityBadge from './AnomalityBadge';
import './PostDetailsModal.css';

const PostDetailsModal = ({ isOpen, onClose, selectedRow }) => {
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedComments, setExpandedComments] = useState({});

  useEffect(() => {
    if (isOpen && selectedRow) {
      fetchPostAndComments();
    }
  }, [isOpen, selectedRow]);

  const fetchPostAndComments = async () => {
    if (!selectedRow || !selectedRow.group_id || !selectedRow.post_id) {
      setError('Недостаточно данных для загрузки поста');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const API_BASE_URL = getAPIUrl();
      
      // Загружаем полные данные поста через новый endpoint
      const postResponse = await axios.get(`${API_BASE_URL}/vk/collection/${selectedRow.group_id}/post/${selectedRow.post_id}`);
      const post = postResponse.data.post;
      post.comments_count = post.comments.length;
      setPost(post);

      // Загружаем комментарии
      const commentsResponse = await axios.get(
        `${API_BASE_URL}/vk/collection/${selectedRow.group_id}/post/${selectedRow.post_id}/comments`,
        { params: { page: 1, limit: 50 } }
      );
      setComments(commentsResponse.data.comments || []);
    } catch (error) {
      console.error('Ошибка загрузки поста/комментариев:', error);
      setError('Не удалось загрузить пост или комментарии');
      
      // Если не удалось загрузить пост, используем данные из строки таблицы как fallback
      const postData = {
        id: selectedRow.post_id,
        group_id: selectedRow.group_id,
        text: selectedRow.text || 'Текст поста недоступен',
        date: selectedRow.date,
        from_id: selectedRow.author_id,
        likes: { count: selectedRow.likes || 0 },
        views: { count: selectedRow.views || 0 },
        comments_count: selectedRow.comments_count || 0,
        attachments: selectedRow.attachments || []
      };
      setPost(postData);
    } finally {
      setLoading(false);
    }
  };

  const formatVkLink = (fromId) => {
    return fromId ? `https://vk.com/${fromId < 0 ? 'club' : 'id'}${Math.abs(fromId)}` : '';
  };

  const truncateText = (text, limit = 10) => {
    if (!text) return 'Нет текста';
    const words = text.split(' ').filter((word) => word.length > 0);
    return words.length > limit ? words.slice(0, limit).join(' ') + '...' : text;
  };

  const toggleCommentText = (commentIndex) => {
    setExpandedComments((prev) => ({ ...prev, [commentIndex]: !prev[commentIndex] }));
  };

  const getAttachmentImage = (attachments) => {
    if (!attachments || !attachments.length) return '';
    const photo = attachments.find((att) => att.type === 'photo');
    if (!photo || !photo.photo) return '';
    const sizes = photo.photo.sizes || [];
    return sizes.find((size) => size.type === 'x')?.url || sizes[sizes.length - 1]?.url || '';
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Детали поста</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          {loading ? (
            <p className="loading">Загрузка...</p>
          ) : error ? (
            <p className="error">{error}</p>
          ) : post ? (
            <div className="post-details">
              {/* Информация о строке таблицы */}
              <div className="table-row-info">
                <h3>Информация о записи</h3>
                <div className="row-details">
                  <p><strong>Группа:</strong> {getGroupTitleById(selectedRow.group_id)} <span style={{opacity:0.6}}>(ID: {selectedRow.group_id})</span></p>
                  <p><strong>Пост ID:</strong> {selectedRow.post_id}</p>
                  {selectedRow.text_anomality !== null && (
                    <p>
                      <strong>Показатель аномальности:</strong> 
                      <AnomalityBadge anomality={selectedRow.text_anomality} size="medium" />
                    </p>
                  )}
                  {selectedRow.date && (
                    <p><strong>Дата:</strong> {formatDate(selectedRow.date)}</p>
                  )}
                  {selectedRow.author_id && (
                    <p><strong>Автор ID:</strong> {selectedRow.author_id}</p>
                  )}
                </div>
              </div>

                             {/* Пост */}
               <div className="post-content">
                 <h3>Пост</h3>
                 <div className="post-text">
                   <p>{post.text || selectedRow.text || 'Нет текста'}</p>
                   {(() => {
                     const imageUrl = getAttachmentImage(post.attachments);
                     return imageUrl && (
                       <img
                         src={imageUrl}
                         alt="Изображение поста"
                         className="post-image"
                         style={{ maxWidth: '100%', maxHeight: '300px', marginTop: '10px' }}
                       />
                     );
                   })()}
                 </div>
                 <div className="post-meta">
                   <span>📅 {formatDate(post.date || selectedRow.date)}</span>
                   <span>👍 {post.likes?.count || selectedRow.likes || 0}</span>
                   <span>👀 {post.views?.count || selectedRow.views || 0}</span>
                   <span>💬 {post.comments_count || selectedRow.comments_count || 0}</span>
                   <a href={formatVkLink(post.from_id || selectedRow.author_id)} target="_blank" rel="noopener noreferrer">Автор</a>
                   <a href={`https://vk.com/wall${post.group_id || selectedRow.group_id}_${post.id || selectedRow.post_id}`} target="_blank" rel="noopener noreferrer">Пост в VK</a>
                 </div>
               </div>

              {/* Комментарии */}
              <div className="comments-section">
                <h3>Комментарии ({comments.length})</h3>
                {comments.length > 0 ? (
                  <div className="comments-list">
                    {comments.map((comment, index) => (
                      <div key={index} className="comment-item">
                        <p onClick={() => toggleCommentText(index)}>
                          {expandedComments[index] ? comment.text : truncateText(comment.text)}
                          {comment.text?.split(' ').filter((word) => word.length > 0).length > 10 && (
                            <span className="read-more">
                              {expandedComments[index] ? ' Свернуть' : ' Читать далее'}
                            </span>
                          )}
                        </p>
                        <div className="comment-meta">
                          <span>📅 {comment.formatted_date || formatDate(comment.date)}</span>
                          <span>👍 {comment.likes || 0}</span>
                          <a href={comment.profile_link || formatVkLink(comment.from_id)} target="_blank" rel="noopener noreferrer">Автор</a>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>Нет комментариев</p>
                )}
              </div>
            </div>
          ) : (
            <p>Пост не найден</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PostDetailsModal; 